import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Package, ArrowRight } from 'lucide-react';
import { useBranding } from '../context/BrandingContext';
import apiService from '../services/apiService';

const CategoryCard = ({ category, onClick, index, viewMode = 'grid' }) => {
  const { t } = useTranslation();
  const { getThemeClasses } = useBranding();
  
  const defaultImage = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80";
  
  const getImageUrl = (icon) => {
    if (!icon) return defaultImage;
    if (icon.startsWith('http')) return icon;
    return `${apiService.baseURL}${icon}`;
  };
  
  const handleImageError = (e) => {
    e.target.src = defaultImage;
  };

  // Vista detallada (horizontal)
  if (viewMode === 'detailed') {
    return (
      <motion.div
        className="bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: index * 0.05 }}
        whileHover={{ x: 5 }}
        onClick={() => onClick(category)}
      >
        <div className="flex flex-col md:flex-row">
          <div className="relative w-full md:w-64 h-48 md:h-auto overflow-hidden flex-shrink-0">
            <img 
              src={getImageUrl(category.icon)} 
              alt={category.name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              onError={handleImageError}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20" />
          </div>
          
          <div className="flex-1 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{t(category.name)}</h3>
                <p className="text-gray-600 text-base leading-relaxed mb-4">
                  {category.description}
                </p>
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-600" />
                  <span className="text-base font-medium text-gray-700">
                    {category.products || category.productCount || 0} {(() => {
                      if (category.name.toLowerCase().includes('hotel')) return 'Hoteles';
                      if (category.name.toLowerCase().includes('servicio')) return 'Servicios';
                      if (category.name.toLowerCase().includes('nieve')) return 'Productos';
                      if (category.name.toLowerCase().includes('all inclusive')) return 'Productos';
                      if (category.name.toLowerCase().includes('programas')) return 'Programas';
                      return 'Productos';
                    })()}
                  </span>
                </div>
              </div>
              <motion.div
                className="p-3 bg-blue-50 rounded-full group-hover:bg-blue-100 transition-colors"
                whileHover={{ rotate: 45 }}
              >
                <ArrowRight className={`w-5 h-5 ${getThemeClasses().accent}`} />
              </motion.div>
            </div>
            
            {/* <div className="pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Disponible para:</span>
                <div className="flex gap-2">
                  <span className={`px-3 py-1 ${getThemeClasses().accent.replace('text-', 'bg-').replace('-600', '-100')} ${getThemeClasses().accent.replace('-600', '-700')} text-sm rounded-full font-medium`}>
                    Agencias
                  </span>
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full font-medium">
                    Admin
                  </span>
                </div>
              </div>
            </div> */}
          </div>
        </div>
      </motion.div>
    );
  }
          <h3 className="text-xl font-bold text-gray-900">{t(category.name)}</h3>
  
  // Vista normal (grid o list)
  return (
    <motion.div
      className="bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -5, scale: 1.02 }}
      onClick={() => onClick(category)}
    >
      <div className="relative h-48 overflow-hidden">
        <img 
          src={getImageUrl(category.icon)} 
          alt={category.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          onError={handleImageError}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-4 left-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-4 h-4" />
            <span className="text-sm font-medium">
              {category.products || category.productCount || 0} {(() => {
                if (category.name.toLowerCase().includes('hotel')) return 'Hoteles';
                if (category.name.toLowerCase().includes('servicio')) return 'Servicios';
                if (category.name.toLowerCase().includes('nieve')) return 'Productos';
                if (category.name.toLowerCase().includes('all inclusive')) return 'Productos';
                if (category.name.toLowerCase().includes('programas')) return 'Programas';
                return 'Productos';
              })()}
            </span>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-bold text-gray-900">{t(category.name)}</h3>
          <motion.div
            className="p-2 bg-blue-50 rounded-full group-hover:bg-blue-100 transition-colors"
            whileHover={{ rotate: 45 }}
          >
            <ArrowRight className={`w-4 h-4 ${getThemeClasses().accent}`} />
          </motion.div>
        </div>
        
        <p className="text-gray-600 text-sm leading-relaxed">
          {category.description}
        </p>
        
        {/* <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Disponible para:</span>
            <div className="flex gap-1">
              <span className={`px-2 py-1 ${getThemeClasses().accent.replace('text-', 'bg-').replace('-600', '-100')} ${getThemeClasses().accent.replace('-600', '-700')} text-xs rounded-full font-medium`}>
                Agencias
              </span>
              <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                Admin
              </span>
            </div>
          </div>
        </div> */}
      </div>
    </motion.div>
  );
};

export default CategoryCard;